import { ChangeDetectionStrategy, Component, inject, Injector, Input, Signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { NgForTrackByPropertyDirective } from '@us/web/shared/data-access';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { TuiAvatarModule } from '@taiga-ui/kit';
import { TuiButtonModule, TuiDialogService, TuiGroupModule, TuiSvgModule } from '@taiga-ui/core';
import {
    CartActions,
    CartMember,
    selectCartMembers,
    selectCartMemberCount,
    selectIsCartShared
} from '@us/web/cart/data-access';
import { Store } from '@ngrx/store';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ShareCartDialogComponent } from '@us/web/cart/ui';
import { AuthActions, selectSessionUser } from '@us/web/auth/data-access';
import { EnterNameDialogComponent } from '@us/web/shared/ui';
import { of, tap } from 'rxjs';
import { TuiButtonOptions } from '@taiga-ui/core/components/button/button.options';

@Component({
    selector: 'us-share-order-btn',
    standalone: true,
    imports: [
        NgForOf,
        NgForTrackByPropertyDirective,
        TranslocoModule,
        TuiAvatarModule,
        TuiButtonModule,
        TuiSvgModule,
        NgIf,
        TuiGroupModule
    ],
    templateUrl: './share-order-btn.component.html',
    styleUrls: ['./share-order-btn.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShareOrderBtnComponent {
    private readonly store = inject(Store);
    private readonly injector = inject(Injector);
    private readonly dialogService = inject(TuiDialogService);
    private readonly translocoService = inject(TranslocoService);

    @Input() size: 'm' | 'l' | 'xl' | 's' | 'xs' = 's';

    readonly isCartShared = this.store.selectSignal(selectIsCartShared);
    readonly members: Signal<CartMember[]> = this.store.selectSignal(selectCartMembers);
    readonly memberCount: Signal<number> = this.store.selectSignal(selectCartMemberCount);

    shareOrder() {
        const user = this.store.selectSignal(selectSessionUser)();
        (!user || !user.name
            ? this.dialogService
                  .open<CartMember>(new PolymorpheusComponent(EnterNameDialogComponent, this.injector), {
                      dismissible: false,
                      size: 's',
                      label: this.translocoService.translate('your_group_order')
                  })
                  .pipe(
                      tap(({ name }) => {
                          this.store.dispatch(AuthActions.updateSessionUserName({ name }));
                      })
                  )
            : of(user)
        )
            .pipe(
                tap({
                    next: value => {
                        this.store.dispatch(
                            CartActions.updateCart({
                                is_shared: true,
                                group_members: [{ ...value, is_owner: true }]
                            })
                        );
                    }
                }),
                tap(() => {
                    this.showAddMoreMemberDialog();
                })
            )
            .subscribe();
    }

    showAddMoreMemberDialog() {
        this.dialogService
            .open<{ name: string }>(new PolymorpheusComponent(ShareCartDialogComponent, this.injector), {
                dismissible: false,
                size: 's',
                label: this.translocoService.translate('your_group_order')
            })
            .subscribe();
    }
}
